import classNames from "classnames";
import * as React from "react";
import { useFormBag } from "react-yup";
import { HelpText, HelpTextProps } from "./HelpText";
import { ValidationError } from "./ValidationError";

type LabelProps = React.DetailedHTMLProps<
  React.LabelHTMLAttributes<HTMLLabelElement>,
  HTMLLabelElement
>;

interface BaseFieldProps {
  id: string;
  name: string;
  label: string;
  labelProps?: LabelProps;
  helpText?: HelpTextProps["helpText"];
  optional?: boolean;
  wrapperProps?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLElement>,
    HTMLElement
  >;
  isInvalid?: boolean;
}

interface PureFieldProps extends FieldProps {
  error?: React.ReactNode;
  touched?: boolean;
  labelSimple?: boolean;
  optionalMessage?: React.ReactNode;
}

export const PureField: React.FC<PureFieldProps> = React.memo(
  ({
    id,
    name,
    label,
    labelProps,
    value,
    optional,
    optionalMessage,
    error,
    touched,
    helpText,
    className,
    autoComplete,
    "aria-describedby": ariaDescribedBy,
    labelSimple,
    ...rest
  }) => {
    return (
      <div className={classNames("form-group", className)}>
        <label htmlFor={id} {...labelProps}>
          {labelSimple ? (
            <>{label}</>
          ) : (
            <>
              {label}{" "}
              {optionalMessage !== undefined ? (
                /* Custom optional message. Accepts null or "" to show nothing  */
                optionalMessage
              ) : optional ? (
                /* Default optional message */
                <small className="text-muted">(optional)</small>
              ) : (
                /* Required field */
                "*"
              )}
            </>
          )}
        </label>
        {helpText && <HelpText id={`${id}-help-text`} helpText={helpText} />}
        <input
          id={id}
          className={classNames("form-control", {
            "is-invalid": error && touched,
          })}
          name={name}
          value={value}
          autoComplete={autoComplete}
          aria-required={!optional}
          aria-invalid={!!error && touched}
          aria-describedby={[
            ariaDescribedBy,
            error && touched ? `${id}-error-message` : null,
            helpText ? `${id}-help-text` : null,
          ]
            .filter((x) => x != null)
            .join(" ")}
          {...rest}
        />
        {error && touched && (
          <ValidationError errorId={`${id}-error-message`} message={error} />
        )}
      </div>
    );
  }
);

type FieldPropsOmit = Omit<
  React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >,
  "id" | "name"
>;

interface FieldProps extends FieldPropsOmit, BaseFieldProps {}

export const Field: React.FC<FieldProps> = (props) => {
  const { name } = props;
  const { getValue, getError, isTouched } = useFormBag();

  const value = getValue(name) || "";
  const error = getError(name);
  const touched = isTouched(name);

  return (
    <PureField
      {...props}
      value={value as string}
      error={error}
      touched={touched}
    />
  );
};

type TextAreaPropsOmit = Omit<
  React.DetailedHTMLProps<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  >,
  "id" | "name"
>;

interface TextAreaProps extends BaseFieldProps, TextAreaPropsOmit {}

interface PureTextAreaProps extends BaseFieldProps, TextAreaPropsOmit {
  error?: string;
  touched?: boolean;
  optionalMessage?: React.ReactNode;
}

export const PureTextArea: React.FC<PureTextAreaProps> = ({
  id,
  name,
  label,
  labelProps,
  helpText,
  optional,
  optionalMessage,
  error,
  touched,
  value,
  ...rest
}) => {
  return (
    <div className="form-group">
      <label htmlFor={id} {...labelProps}>
        {label}{" "}
        {optionalMessage !== undefined ? (
          /* Custom optional message. Accepts null or "" to show nothing */
          optionalMessage
        ) : optional ? (
          /* Default optional message */
          <small className="text-muted">(optional)</small>
        ) : (
          /* Required field */
          "*"
        )}
      </label>
      {helpText && <HelpText id={`${id}-help-text`} helpText={helpText} />}
      <textarea
        id={id}
        className={classNames("form-control", {
          "is-invalid": error && touched,
        })}
        name={name}
        value={value as string}
        aria-invalid={!!error && touched}
        aria-required={!optional}
        aria-describedby={`${error && touched ? `${id}-error-message ` : ""}${
          helpText ? `${id}-help-text` : ""
        }`}
        {...rest}
      />
      {error && touched && (
        <ValidationError errorId={`${id}-error-message`} message={error} />
      )}
    </div>
  );
};

export const TextArea: React.FC<TextAreaProps> = (props) => {
  const { name } = props;
  const { getValue, getError, isTouched } = useFormBag();

  const value = getValue(name) || "";
  const error = getError(name);
  const touched = isTouched(name);

  return (
    <PureTextArea
      {...props}
      value={value as string}
      error={error}
      touched={touched}
    />
  );
};

type SelectPropsOmit = Omit<
  React.DetailedHTMLProps<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    HTMLSelectElement
  >,
  "id" | "name"
> & {
  isFullWidth?: boolean;
};

interface SelectProps extends BaseFieldProps, SelectPropsOmit {}

export const Select: React.FC<SelectProps> = ({
  name,
  id,
  label,
  labelProps,
  wrapperProps,
  helpText,
  optional,
  children,
  isFullWidth,
  ...rest
}) => {
  const { getValue, getError, isTouched } = useFormBag();

  const value = getValue(name) || "";
  const error = getError(name);
  const touched = isTouched(name);

  return (
    <div className={classNames(wrapperProps?.className, "form-group")}>
      <label htmlFor={id} {...labelProps}>
        {label}{" "}
        {optional ? <small className="text-muted">(optional)</small> : "*"}
      </label>
      {helpText && <HelpText id={`${id}-help-text`} helpText={helpText} />}
      <select
        id={id}
        className={classNames("custom-select", {
          "is-invalid": error && touched,
        })}
        css={isFullWidth === false && "width: unset;"}
        name={name}
        value={value as string}
        aria-invalid={!!error && touched}
        aria-required={!optional}
        aria-describedby={`${error && touched ? `${id}-error-message ` : ""}${
          helpText ? `${id}-help-text` : ""
        }`}
        {...rest}
      >
        {children}
      </select>
      {error && touched && (
        <ValidationError errorId={`${id}-error-message`} message={error} />
      )}
    </div>
  );
};

type CheckPropsOmit = Omit<
  React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >,
  "id" | "name" | "type" | "ref"
>;
interface CheckProps
  extends Omit<BaseFieldProps, "helpText" | "optional" | "labelProps">,
    CheckPropsOmit {
  type: "radio" | "checkbox" | "switch";
  inline?: boolean;
}

export const Check = React.forwardRef<HTMLInputElement, CheckProps>(
  (
    { id, name, type, label, inline = true, wrapperProps, isInvalid, ...rest },
    ref
  ) => {
    return (
      <div
        className={classNames(
          "custom-control",
          `custom-${type}`,
          {
            "custom-control-inline": inline,
          },
          wrapperProps?.className
        )}
      >
        <input
          ref={ref}
          name={name}
          type={type === "switch" ? "checkbox" : type}
          id={id}
          className={classNames("custom-control-input", {
            "is-invalid": isInvalid,
          })}
          {...rest}
        />
        <label htmlFor={id} className="custom-control-label">
          {label}
        </label>
      </div>
    );
  }
);

/*
  TODO : This is a special case for when we want to do something on unmount
  Figure out potentially a better way/move to SKV.AdminAU/pages/Opportunity?
*/
interface CheckWithUnmountProps extends CheckProps {
  onUnmount: () => void;
}

export const CheckWithUnmount = React.forwardRef<
  HTMLInputElement,
  CheckWithUnmountProps
>(({ onUnmount, ...props }, ref) => {
  React.useEffect(() => {
    return onUnmount;
  }, []);

  return <Check ref={ref} {...props} />;
});
